import useSWR from 'swr';
import { cn } from '@/lib/utils';
import type { MentorFillResponse } from '@/types/selection';

// app/(public)/26-{ar,mx}/_components/fill-bars.tsx — the two Next copies are
// byte-identical and all four pages of this unit render it, so it is one component here
// (PORTING.md U8). The mentor and track responses have the same shape.

const fetcher = (url: string) => fetch(url).then((r) => r.json());

interface FillBarsProps {
  endpoint: string;
  title: string;
  emptyLabel: string;
}

export function FillBars({ endpoint, title, emptyLabel }: FillBarsProps) {
  const { data } = useSWR<MentorFillResponse>(endpoint, fetcher, {
    refreshInterval: 1000,
    refreshWhenHidden: true,
  });

  const limit = data?.limit ?? null;
  const items = data?.items ?? [];
  const maxRows = Math.max(limit ?? 0, ...items.map((i) => i.teamSlugs.length));
  const totalRows = Math.max(maxRows, 1);

  return (
    <div className="flex h-screen flex-col bg-background p-4 text-foreground md:p-6">
      <div className="mb-4 flex shrink-0 items-baseline justify-between md:mb-6">
        <h1 className="font-bold text-2xl tracking-tight md:text-3xl">
          {title}
        </h1>
        <div className="text-muted-foreground text-sm tabular-nums">
          {limit !== null ? `límite: ${limit}` : 'sin límite'} · {items.length}{' '}
          {items.length === 1 ? 'item' : 'items'}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-muted-foreground">{emptyLabel}</div>
      ) : (
        <div
          className="grid min-h-0 flex-1 gap-2 md:gap-3"
          style={{
            gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`,
          }}
        >
          {items.map((item) => (
            <FillBar
              key={item.id}
              item={item}
              limit={limit}
              totalRows={totalRows}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FillBar({
  item,
  limit,
  totalRows,
}: {
  item: MentorFillResponse['items'][number];
  limit: number | null;
  totalRows: number;
}) {
  const count = item.teamSlugs.length;
  const isFull = limit !== null && count >= limit;
  const isOver = limit !== null && count > limit;

  return (
    <div className="flex min-w-0 flex-col items-stretch">
      <div className="relative flex min-h-0 flex-1 flex-col-reverse overflow-hidden rounded-md border border-border bg-card">
        {limit !== null && (
          <div
            className="absolute right-0 left-0 z-10 border-foreground/60 border-t-2 border-dashed"
            style={{ bottom: `${(limit / totalRows) * 100}%` }}
          >
            <span className="-top-5 absolute right-1 font-mono text-[10px] text-foreground/70">
              {limit}
            </span>
          </div>
        )}
        {item.teamSlugs.map((slug, idx) => (
          <div
            key={slug}
            className={cn(
              'flex items-center justify-center truncate border-background/30 border-t px-1 font-mono text-[10px] md:text-xs',
              isOver && idx >= (limit ?? 0)
                ? 'bg-destructive text-destructive-foreground'
                : isFull
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-primary/80 text-primary-foreground',
            )}
            style={{ height: `${100 / totalRows}%` }}
            title={slug}
          >
            {slug}
          </div>
        ))}
      </div>
      <div className="mt-2 text-center">
        <div
          className="truncate font-medium text-xs md:text-sm"
          title={item.name}
        >
          {item.name}
        </div>
        <div className="text-muted-foreground text-xs tabular-nums">
          {count}
          {limit !== null ? `/${limit}` : ''}
        </div>
      </div>
    </div>
  );
}
